import { Button, Card, Form, Input, Typography, notification } from '@pankod/refine-antd';
import { useState } from 'react';
import { syncSingleProductInventory } from 'services/inventory';

const { Title, Paragraph, Text } = Typography;

interface IInventorySyncFormValues {
  productId: string;
  warehouseId: string;
}

export const InventorySync: React.FC = () => {
  const [form] = Form.useForm<IInventorySyncFormValues>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [lastResult, setLastResult] = useState<string | null>(null);

  const onFinish = async (values: IInventorySyncFormValues) => {
    setIsSubmitting(true);
    setLastResult(null);
    try {
      const result = await syncSingleProductInventory({
        productId: values.productId.trim(),
        warehouseId: values.warehouseId.trim(),
      });
      setLastResult(result);
      notification.success({
        message: 'Inventory synced',
        description: `Product ${values.productId.trim()} at warehouse ${values.warehouseId.trim()} has been reconciled against its purchase/sales/manufacture history.`,
      });
    } catch (err: any) {
      notification.error({
        message: 'Sync failed',
        description: err.message,
      });
    }
    setIsSubmitting(false);
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        padding: '3rem 1rem',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 480 }}>
        <Title level={4}>Inventory Sync</Title>
        <Paragraph type="secondary">
          Recalculates a single product&apos;s cached inventory quantity (for one warehouse) from
          its purchase, sales, transfer, damage, return, and manufacture history, and corrects it
          if it has drifted from the ledger.
        </Paragraph>
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Form.Item
            label="Product ID"
            name="productId"
            rules={[
              { required: true, message: 'Product ID is required' },
              { whitespace: true, message: 'Product ID cannot be blank' },
            ]}
          >
            <Input placeholder="e.g. e506fca6-90bd-44b6-8a53-1eecbacb70db" />
          </Form.Item>
          <Form.Item
            label="Warehouse ID"
            name="warehouseId"
            rules={[
              { required: true, message: 'Warehouse ID is required' },
              { whitespace: true, message: 'Warehouse ID cannot be blank' },
            ]}
          >
            <Input placeholder="e.g. AZ_VETLBQC" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: '1.5rem' }}>
            <Button type="primary" htmlType="submit" loading={isSubmitting} block>
              Sync Inventory
            </Button>
          </Form.Item>
        </Form>
        {lastResult && (
          <Paragraph style={{ marginTop: '1rem', marginBottom: 0 }}>
            <Text type="secondary">Last result: {lastResult}</Text>
          </Paragraph>
        )}
      </Card>
    </div>
  );
};
